import { type CSSProperties } from 'react'

import { useLocalStorageState } from '@/lib/use-local-storage-state'

const SIZE_KEY = 'vantage.entity-selector-size'
const HEIGHT_SHARE = 0.75

export const PANEL_WIDTH = { default: 448, min: 320, max: 720 }
export const PANEL_HEIGHT = { min: 320, max: 900 }

type Bounds = { min: number; max: number }
type PanelSize = { width: number; height: number }

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function clamp(value: number, bounds: Bounds): number {
  return Math.min(bounds.max, Math.max(bounds.min, value))
}

function parseSize(raw: string): PanelSize | undefined {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return undefined
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined
  const { width, height } = parsed as Record<string, unknown>
  if (!isFiniteNumber(width) || !isFiniteNumber(height)) return undefined
  return {
    width: clamp(width, PANEL_WIDTH),
    height: clamp(height, PANEL_HEIGHT),
  }
}

function viewportHeight(): number {
  return clamp(Math.round(window.innerHeight * HEIGHT_SHARE), PANEL_HEIGHT)
}

export function useEntitySelectorSize(): {
  style: CSSProperties
  size: PanelSize
  setSize: (next: PanelSize) => void
  setWidth: (width: number) => void
  setHeight: (height: number) => void
} {
  const [stored, setStored] = useLocalStorageState<PanelSize | null>(
    SIZE_KEY,
    parseSize,
    JSON.stringify,
    null,
  )
  const size = stored ?? {
    width: PANEL_WIDTH.default,
    height: viewportHeight(),
  }

  const setSize = (next: PanelSize) =>
    setStored({
      width: clamp(next.width, PANEL_WIDTH),
      height: clamp(next.height, PANEL_HEIGHT),
    })

  return {
    style: stored
      ? ({
          '--panel-width': `${stored.width}px`,
          '--panel-height': `${stored.height}px`,
        } as CSSProperties)
      : {},
    size,
    setSize,
    setWidth: (width) => setSize({ ...size, width }),
    setHeight: (height) => setSize({ ...size, height }),
  }
}
